import { formatInTimeZone } from 'date-fns-tz';
import {
  storeEndOfCalendarDay,
  storeMatchRangeLastMonth,
  storeMatchRangeLastNCalendarDays,
  storeMatchRangeLastWeek,
  storeMatchRangeLastYear,
  storeMatchRangeThisMonth,
  storeMatchRangeThisWeek,
  storeMatchRangeThisYear,
  storeMatchRangeToday,
  storeMatchRangeYesterday,
  storeStartOfCalendarDay
} from './storeDateRange';

/**
 * Product Analysis view state <-> URL query string.
 *
 * The page keeps its filters in React state and seeds every dimension to the full facet universe,
 * so "no filter" means "everything selected". Only the dimensions that have actually been narrowed
 * belong in the URL, which is exactly what `buildFilterQueryParams` computes — callers pass its
 * output in as `narrowedFilters` so this module stays free of a circular import on the view.
 */

export const CUSTOM_RANGE_LABEL = 'Custom Range';

/** Named date presets, shared by the date picker and URL restore so they can't drift apart. */
export const RANGE_PRESETS = [
  { label: 'Last 7 Days', resolve: (tz) => storeMatchRangeLastNCalendarDays(tz, 7) },
  { label: 'Last 14 Days', resolve: (tz) => storeMatchRangeLastNCalendarDays(tz, 14) },
  { label: 'Last 30 Days', resolve: (tz) => storeMatchRangeLastNCalendarDays(tz, 30) },
  { label: 'This Week', resolve: (tz) => storeMatchRangeThisWeek(tz) },
  { label: 'Last Week', resolve: (tz) => storeMatchRangeLastWeek(tz) },
  { label: 'This Month', resolve: (tz) => storeMatchRangeThisMonth(tz) },
  { label: 'Last Month', resolve: (tz) => storeMatchRangeLastMonth(tz) },
  { label: 'This Year', resolve: (tz) => storeMatchRangeThisYear(tz) },
  { label: 'Last Year', resolve: (tz) => storeMatchRangeLastYear(tz) }
];

/**
 * StoreDateRangeFilter renders these two itself rather than from the `options` it is handed, so they
 * are restorable but must stay out of `RANGE_PRESETS` or the popover would list them twice.
 */
const BUILT_IN_PRESETS = [
  { label: 'Today', resolve: (tz) => storeMatchRangeToday(tz) },
  { label: 'Yesterday', resolve: (tz) => storeMatchRangeYesterday(tz) }
];

const RESTORABLE_PRESETS = [...RANGE_PRESETS, ...BUILT_IN_PRESETS];

export const DEFAULT_RANGE_LABEL = 'Last 7 Days';
export const DEFAULT_TABLE_SORT = { sort_by: 'qty', sort_dir: -1 };

const SORT_FIELDS = new Set(['qty', 'revenue', 'opportunity', 'lost', 'sellout']);
const YMD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * URL key -> `filters` key. Keys match the analysis API body (`product_type` is singular there)
 * so the query string reads the same as the request it produces.
 */
const FILTER_DIMENSIONS = [
  {
    urlKey: 'product_ids',
    filterKey: 'product_ids',
    universe: (facets) => (facets.titles || []).map((t) => String(t?.id))
  },
  {
    urlKey: 'collection_ids',
    filterKey: 'collection_ids',
    universe: (facets) => (facets.collections || []).map((c) => String(c?.id))
  },
  {
    urlKey: 'product_type',
    filterKey: 'product_types',
    universe: (facets) => (facets.product_types || []).map(String)
  },
  {
    urlKey: 'tags',
    filterKey: 'tags',
    universe: (facets) => (facets.tags || []).map(String)
  },
  {
    urlKey: 'sales_channels',
    filterKey: 'sales_channels',
    universe: (facets) => (facets.sales_channels || []).map(String)
  },
  {
    urlKey: 'location_ids',
    filterKey: 'location_ids',
    universe: (facets) => (facets.locations || []).map((l) => String(l?.id))
  }
];

/** Detail page only persists range + channel + location (no table sort/page/SKU dims). */
const DETAIL_FILTER_URL_KEYS = new Set(['sales_channels', 'location_ids']);

function isNamedPreset(label) {
  return RESTORABLE_PRESETS.some((p) => p.label === label);
}

function skuUniverseFor(facets, skuMode) {
  const list = (skuMode === 'variant' ? facets?.skus : facets?.master_skus) || [];
  return list.map(String);
}

/**
 * Range-only query string. Analytics sources (Shopify, Meta Ads, …) share the date range but not
 * filters, so the source switcher carries just this.
 */
export function serializeRangeState({ activeRange, match, timezone }) {
  const params = new URLSearchParams();
  if (isNamedPreset(activeRange)) {
    params.set('range', activeRange);
  } else if (timezone && match?.start && match?.end) {
    params.set('start', formatInTimeZone(match.start, timezone, 'yyyy-MM-dd'));
    params.set('end', formatInTimeZone(match.end, timezone, 'yyyy-MM-dd'));
  }
  return params;
}

/**
 * Serialize the current view into a query string.
 *
 * Presets are stored by name and re-resolved on load, so a shared link keeps meaning "last 30
 * days" rather than freezing a window; custom ranges store the store-calendar days instead.
 * Values equal to the page defaults are omitted to keep the common URL short.
 */
export function serializeViewState({
  narrowedFilters,
  skuMode,
  activeRange,
  match,
  timezone,
  tableSort,
  tablePage
}) {
  const params = serializeRangeState({ activeRange, match, timezone });

  const narrowed = narrowedFilters || {};
  for (const dim of FILTER_DIMENSIONS) {
    const values = narrowed[dim.urlKey];
    if (!Array.isArray(values)) continue;
    // An empty array means "nothing selected", which the page can't load anyway; leaving the key
    // out restores as "all" instead of a dead end.
    for (const value of values) params.append(dim.urlKey, String(value));
  }
  if (Array.isArray(narrowed.skus)) {
    for (const sku of narrowed.skus) params.append('skus', String(sku));
  }
  // Unlike the API body, carry the mode even when the SKU list isn't narrowed so the toggle survives.
  if (skuMode === 'variant') params.set('sku_mode', 'variant');

  const sortBy = tableSort?.sort_by;
  const sortDir = Number(tableSort?.sort_dir);
  if (sortBy && sortBy !== DEFAULT_TABLE_SORT.sort_by) params.set('sort_by', sortBy);
  if (sortDir === 1 || sortDir === -1) {
    if (sortDir !== DEFAULT_TABLE_SORT.sort_dir) params.set('sort_dir', String(sortDir));
  }

  const page = Number(tablePage);
  if (Number.isFinite(page) && page > 1) params.set('page', String(Math.floor(page)));

  return params;
}

/**
 * Product detail view <-> URL. Same range encoding as the list; only sales_channels and
 * location_ids are written so list-only dims (product_ids, sort, page) never clutter or
 * poison a shared product link.
 */
export function serializeDetailViewState({ narrowedFilters, activeRange, match, timezone }) {
  const params = serializeRangeState({ activeRange, match, timezone });

  const narrowed = narrowedFilters || {};
  for (const key of DETAIL_FILTER_URL_KEYS) {
    const values = narrowed[key];
    if (!Array.isArray(values)) continue;
    for (const value of values) params.append(key, String(value));
  }

  return params;
}

/**
 * Read a query string into the pieces the page hydrates from. Returns nulls for anything absent or
 * malformed so callers can fall back to their own defaults.
 */
export function parseViewState(searchParams) {
  const sp =
    searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams || '');

  const values = (key) =>
    sp
      .getAll(key)
      .map((v) => String(v).trim())
      .filter(Boolean);

  const filters = {};
  for (const dim of FILTER_DIMENSIONS) {
    const list = values(dim.urlKey);
    if (list.length) filters[dim.urlKey] = list;
  }
  const skus = values('skus');
  if (skus.length) filters.skus = skus;
  if (sp.get('sku_mode') === 'variant') filters.sku_mode = 'variant';

  const rangeLabel = sp.get('range');
  const startYmd = sp.get('start');
  const endYmd = sp.get('end');

  const sortBy = sp.get('sort_by');
  const rawSortDir = sp.get('sort_dir');
  const sortDir = rawSortDir === '1' ? 1 : rawSortDir === '-1' ? -1 : null;
  const hasSort = SORT_FIELDS.has(sortBy) || sortDir != null;

  const page = Number.parseInt(sp.get('page'), 10);

  return {
    filters: Object.keys(filters).length ? filters : null,
    range: {
      label: isNamedPreset(rangeLabel) ? rangeLabel : null,
      startYmd: YMD.test(startYmd || '') ? startYmd : null,
      endYmd: YMD.test(endYmd || '') ? endYmd : null
    },
    sort: hasSort
      ? {
          sort_by: SORT_FIELDS.has(sortBy) ? sortBy : DEFAULT_TABLE_SORT.sort_by,
          sort_dir: sortDir ?? DEFAULT_TABLE_SORT.sort_dir
        }
      : null,
    page: Number.isFinite(page) && page > 1 ? page : null
  };
}

/**
 * Turn the parsed range back into `{ match, activeRange }`, or null when the URL carried no usable
 * range. Custom ranges round-trip exactly because `match` bounds are already store day boundaries.
 */
export function resolveRangeFromUrl(timezone, range) {
  if (!timezone || !range) return null;

  const preset = RESTORABLE_PRESETS.find((p) => p.label === range.label);
  if (preset) return { match: preset.resolve(timezone), activeRange: preset.label };

  if (range.startYmd && range.endYmd) {
    const start = storeStartOfCalendarDay(timezone, range.startYmd);
    const end = storeEndOfCalendarDay(timezone, range.endYmd);
    const usable =
      start instanceof Date &&
      end instanceof Date &&
      !Number.isNaN(start.getTime()) &&
      !Number.isNaN(end.getTime()) &&
      start <= end;
    if (usable) return { match: { start, end }, activeRange: CUSTOM_RANGE_LABEL };
  }

  return null;
}

/**
 * Layer URL filters over the all-selected seed built from the first filter-candidates response.
 *
 * Requested values are intersected with the facet universe. When nothing survives — a stale link,
 * a value that dropped out of the range, another store's ids — the dimension stays at "all" rather
 * than becoming an empty selection, which `filtersAllowAnalytics` treats as unloadable and would
 * leave the page spinning.
 */
export function applyUrlFiltersToSeed(seed, urlFilters, facets) {
  if (!urlFilters) return seed;

  const next = { ...seed };
  const skuMode = urlFilters.sku_mode === 'variant' ? 'variant' : 'master';
  const skuUniverse = skuUniverseFor(facets, skuMode);

  // Variant mode changes what "all SKUs" means, so rebase the seed before narrowing it.
  next.sku_mode = skuMode;
  next.skus = skuUniverse;

  for (const dim of FILTER_DIMENSIONS) {
    const requested = urlFilters[dim.urlKey];
    if (!Array.isArray(requested) || !requested.length) continue;
    const universe = new Set(dim.universe(facets || {}));
    const kept = requested.map(String).filter((v) => universe.has(v));
    if (kept.length) next[dim.filterKey] = kept;
  }

  if (Array.isArray(urlFilters.skus) && urlFilters.skus.length) {
    const universe = new Set(skuUniverse);
    const kept = urlFilters.skus.map(String).filter((v) => universe.has(v));
    if (kept.length) next.skus = kept;
  }

  return next;
}
